import React, { useState, useEffect, useCallback } from 'react';
import { diffWords } from 'diff';
import CaPlusIntakeTab from './CaPlusIntakeTab';

// Word-level tracked-changes display — used for AI findings a reviewer has
// edited but not yet accepted. Struck-through red = removed, underlined
// green = added, matching the classic Word tracked-changes convention.
function TrackedChangesText({ original, edited }) {
  if (!original || original === edited) return <>{edited}</>;
  const parts = diffWords(original, edited);
  return (
    <>
      {parts.map((part, i) => {
        if (part.added) {
          return <span key={i} style={{ color: '#166534', textDecoration: 'underline', backgroundColor: '#f0fdf4' }}>{part.value}</span>;
        }
        if (part.removed) {
          return <span key={i} style={{ color: '#dc2626', textDecoration: 'line-through', backgroundColor: '#fef2f2' }}>{part.value}</span>;
        }
        return <span key={i}>{part.value}</span>;
      })}
    </>
  );
}

// ─── CA+ Tab ──────────────────────────────────────────────────────────────
// Matches the pattern of DrawingCompare.js / DesignVsShopDrawing.js: a
// standalone component imported into App.js and wired into the tab system.
//
// This single tab covers both roles: regular reviewers see the 3-button
// dashboard below; users on FirmProfile.caPlusAdminEmails additionally see a
// "Switch to Admin View" button that renders CaPlusIntakeTab.js (Natalie's
// queue + intake screen) in place of the dashboard. No separate App.js tab
// needed for the admin side.
//
// Wire-up in App.js:
//   import CaPlusTab from './CaPlusTab';
//   ...add to ALL_TABS:
//   { id: 'ca-plus', label: 'CA+', icon: '📐', featureKey: 'caPlusSubmittals' }
//   ...add to getActiveTabContent():
//   case 'ca-plus':
//     return (
//       <>
//         <RecentDocumentsPanel activeTab={activeTab} />
//         <CaPlusTab user={user} getFlag={getFlag} />
//       </>
//     );
//   ...add to getTabDescription():
//   case 'ca-plus':
//     return "Review Submittals, RFIs, and Field Reports with AI-drafted comments.";
// ─────────────────────────────────────────────────────────────────────────

const API_BASE = 'https://apispeccheck.zonetap.com';

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '16px',
  },
  buttonRow: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
  bigButton: (active) => ({
    flex: '1 1 160px',
    padding: '20px',
    borderRadius: '10px',
    border: active ? '2px solid #D4AF37' : '2px solid #e5e7eb',
    backgroundColor: active ? '#fffbeb' : 'white',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
  }),
  bigButtonLabel: { fontSize: '15px', fontWeight: '700', color: '#000000', marginBottom: '4px' },
  bigButtonCount: { fontSize: '24px', fontWeight: '800', color: '#D4AF37' },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    backgroundColor: '#fafafa',
  },
  listRowTitle: { fontSize: '14px', fontWeight: '600', color: '#000000' },
  listRowMeta: { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  badge: (color) => ({
    fontSize: '11px',
    fontWeight: '700',
    padding: '3px 10px',
    borderRadius: '12px',
    backgroundColor: color + '22',
    color,
  }),
  backLink: { color: '#D4AF37', fontWeight: '700', cursor: 'pointer', fontSize: '15px', marginBottom: '16px', display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.03em' },
  sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#000000', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #D4AF37' },
  textarea: {
    width: '100%', minHeight: '90px', padding: '10px', border: '2px solid #d1d5db',
    borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
  },
  input: {
    width: '100%', padding: '10px', border: '2px solid #d1d5db', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '12px',
  },
  select: {
    width: '100%', padding: '10px', border: '2px solid #d1d5db', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '12px', backgroundColor: 'white',
  },
  primaryBtn: {
    backgroundColor: '#D4AF37', color: '#000000', fontWeight: '700', padding: '10px 20px',
    borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px',
  },
  secondaryBtn: {
    backgroundColor: 'white', color: '#374151', fontWeight: '600', padding: '10px 18px',
    borderRadius: '8px', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '14px',
  },
  stampGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', marginTop: '12px' },
  stampOption: (selected) => ({
    padding: '12px 14px', borderRadius: '8px', border: selected ? '2px solid #D4AF37' : '2px solid #e5e7eb',
    backgroundColor: selected ? '#fffbeb' : 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#000000',
  }),
};

const STATUS_COLORS = { received: '#3B82F6', in_review: '#F59E0B', returned: '#22C55E' };

export default function CaPlusTab({ user, getFlag }) {
  const userId = user?.email || user?.sub || 'anonymous';
  const reviewerName = user?.name || userId;

  const [appName, setAppName] = useState('CA+');
  const [dueDateWarningDays, setDueDateWarningDays] = useState(3);
  const [stampOptions, setStampOptions] = useState([]);
  const [offices, setOffices] = useState([]);
  const [officeFilter, setOfficeFilter] = useState('');

  const [view, setView] = useState('dashboard'); // dashboard | list | detail | intake
  const [adminView, setAdminView] = useState(false);
  const [isCaPlusAdmin, setIsCaPlusAdmin] = useState(false);
  const [listType, setListType] = useState(null); // 'submittals' | 'rfis' | 'fors'
  const [dashboardData, setDashboardData] = useState({ counts: {}, submittals: [], rfis: [], fors: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [listSearch, setListSearch] = useState('');
  const [listSort, setListSort] = useState(() => {
    try {
      return localStorage.getItem(`caplus_listSort_${userId}`) || 'date-desc';
    } catch {
      return 'date-desc';
    }
  });

  // Persist the reviewer's chosen sort so it's remembered next time they
  // open a list, instead of always resetting to Newest First. Keyed per
  // user so a shared machine doesn't mix up different reviewers' preferences.
  useEffect(() => {
    try {
      localStorage.setItem(`caplus_listSort_${userId}`, listSort);
    } catch {
      // localStorage can throw in some browser privacy modes — harmless
      // to skip persistence in that case, sort still works for the session.
    }
  }, [listSort, userId]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [bluebeamFile, setBluebeamFile] = useState(null);
  const [finalizingExternal, setFinalizingExternal] = useState(false);

  const [drawingFile, setDrawingFile] = useState(null);
  const [addingDrawing, setAddingDrawing] = useState(false);
  const [drawingAddedMsg, setDrawingAddedMsg] = useState(null);
  const [specFile, setSpecFile] = useState(null);
  const [addingSpec, setAddingSpec] = useState(false);
  const [specAddedMsg, setSpecAddedMsg] = useState(null);
  const [clearingDrawings, setClearingDrawings] = useState(false);

  const rfisEnabled = getFlag ? getFlag('caPlusRFIs').enabled !== false : true;
  const forsEnabled = getFlag ? getFlag('caPlusFORs').enabled !== false : true;

  // Load firm profile (stamp options, app name, offices) — reuses the existing
  // /api/firm-profile route, no new endpoint needed.
  useEffect(() => {
    if (!userId || userId === 'anonymous') return;
    fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((data) => {
        const p = data.profile || {};
        if (p.caPlusAppName) setAppName(p.caPlusAppName);
        setStampOptions(p.caPlusStampOptions || []);
        setOffices(p.offices || []);
        setDueDateWarningDays(typeof p.caPlusDueDateWarningDays === 'number' ? p.caPlusDueDateWarningDays : 3);
      })
      .catch(() => {});
  }, [userId]);


  const loadDashboard = useCallback(() => {
    if (!userId || userId === 'anonymous') return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ userId, reviewerName });
    if (officeFilter) params.set('office', officeFilter);
    fetch(`${API_BASE}/api/ca-plus/dashboard?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load dashboard');
        return r.json();
      })
      .then((data) => {
        setDashboardData(data);
        setIsCaPlusAdmin(!!data.isCaPlusAdmin);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [userId, reviewerName, officeFilter]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Push an initial history entry for the dashboard itself, so the very
  // first back-button press — before ever opening a list or detail screen —
  // doesn't fall straight through to leaving the app. Per Randy, it's easy
  // to unintentionally close CA+ with one stray back press.
  useEffect(() => {
    window.history.pushState({ caPlusView: 'dashboard' }, '');
  }, []);

  const openList = (type) => {
    setListType(type);
    setView('list');
    window.history.pushState({ caPlusView: 'list' }, '');
  };

  const openDetail = (item) => {
    setSelectedItem(item);
    setSelectedStamp(item.stampActionId || null);
    setNewComment('');
    setView('detail');
    window.history.pushState({ caPlusView: 'detail' }, '');
  };

  const backToDashboard = () => {
    setView('dashboard');
    setSelectedItem(null);
    setListSearch('');
    loadDashboard();
  };

  // Browser back button steps back through CA+'s own screens (detail →
  // list → dashboard) instead of leaving the app entirely — since view
  // changes never touch the actual URL, the browser has no other way to
  // know these "pages" exist.
  useEffect(() => {
    const handlePopState = () => {
      if (view === 'detail') {
        setView('list');
      } else if (view === 'list') {
        backToDashboard();
      } else {
        // Already on dashboard — re-assert our own history entry instead
        // of letting the back button leave the app entirely. CA+ has no
        // "exit" screen of its own, so a stray back press here previously
        // closed the whole thing — exactly what Randy reported.
        window.history.pushState({ caPlusView: 'dashboard' }, '');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [view]);

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');

  const [editingAiCommentId, setEditingAiCommentId] = useState(null);
  const [editAiCommentText, setEditAiCommentText] = useState('');

  const toggleAcceptAiComment = async (commentId, currentlyAccepted) => {
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/ai-comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, accepted: !currentlyAccepted }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update finding');
      setSelectedItem(data.submittal || data.rfi);
    } catch (e) {
      alert(e.message);
    }
  };

  const toggleRejectAiComment = async (commentId, currentlyRejected) => {
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/ai-comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        // Rejecting also clears accepted, since a rejected finding can't
        // simultaneously be an accepted one — they're mutually exclusive.
        body: JSON.stringify({ userId, rejected: !currentlyRejected, accepted: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update finding');
      setSelectedItem(data.submittal || data.rfi);
    } catch (e) {
      alert(e.message);
    }
  };

  const updateStampLocation = async (submittalType) => {
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/submittals/${selectedItem._id}/stamp-location`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, submittalType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update stamp location');
      setSelectedItem(data.submittal);
    } catch (e) {
      alert(e.message);
    }
  };

  const startEditAiComment = (comment) => {
    setEditingAiCommentId(comment._id);
    setEditAiCommentText(comment.text ?? comment.response);
  };

  const saveEditAiComment = async () => {
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/ai-comments/${editingAiCommentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, text: editAiCommentText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save edit');
      setSelectedItem(data.submittal || data.rfi);
      setEditingAiCommentId(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/comments/${commentId}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete comment');
      setSelectedItem(data.submittal || data.rfi);
    } catch (e) {
      alert(e.message);
    }
  };

  const toggleAcceptComment = async (commentId, currentlyAccepted) => {
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, accepted: !currentlyAccepted }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update comment');
      setSelectedItem(data.submittal || data.rfi);
    } catch (e) {
      alert(e.message);
    }
  };

  const startEditComment = (comment) => {
    setEditingCommentId(comment._id);
    setEditCommentText(comment.text);
  };

  const saveEditComment = async () => {
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/comments/${editingCommentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, text: editCommentText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save edit');
      setSelectedItem(data.submittal || data.rfi);
      setEditingCommentId(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedItem) return;
    setActionLoading(true);
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/comments`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, text: newComment.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add comment');
      setSelectedItem(data.submittal || data.rfi);
      setNewComment('');
    } catch (e) {
      alert(e.message);
    } finally {
      setActionLoading(false);
    }
  };

    const addSpec = async () => {
    if (!specFile || !selectedItem) return;
    setAddingSpec(true);
    setSpecAddedMsg(null);
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('spec', specFile);
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/add-spec`, {
        method: 'PATCH',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add spec file');
      setSpecAddedMsg(data.message || 'Spec file added — re-analysis started.');
      setSpecFile(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setAddingSpec(false);
    }
  };

    const addDrawing = async () => {
    if (!drawingFile || !selectedItem) return;
    setAddingDrawing(true);
    setDrawingAddedMsg(null);
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('drawing', drawingFile);
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/add-drawing`, {
        method: 'PATCH',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add drawing');
      setDrawingAddedMsg(data.message || 'Drawing added — re-analysis started.');
      setDrawingFile(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setAddingDrawing(false);
    }
  };

    const clearDrawings = async () => {
    if (!selectedItem) return;
    if (!window.confirm('Remove all attached drawings from this item? This does not undo anything already analyzed — it just clears the list so the next drawing you add starts fresh.')) return;
    setClearingDrawings(true);
    const kind = listType === 'rfis' ? 'rfis' : 'submittals';
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/${kind}/${selectedItem._id}/clear-drawings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to clear drawings');
      setSelectedItem(data.submittal || data.rfi);
      setDrawingAddedMsg(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setClearingDrawings(false);
    }
  };

    const downloadBase64Pdf = (base64, filename) => {
    if (!base64) return;
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'report.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const applyStamp = async () => {
    if (!selectedStamp || !selectedItem) return;
    const items = selectedItem.aiDraftComments || [];
    const unacceptedCount = items.filter((c) => !c.accepted && !c.rejected).length;
    if (unacceptedCount > 0) {
      alert(`Please review and accept all ${items.length} AI findings before returning this submittal (${unacceptedCount} still need review).`);
      return;
    }
    const comments = selectedItem.reviewerComments || [];
    const unacceptedComments = comments.filter((c) => !c.accepted).length;
    if (unacceptedComments > 0) {
      alert(`Please accept all ${comments.length} reviewer comments before returning this submittal (${unacceptedComments} still need review).`);
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/submittals/${selectedItem._id}/stamp`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, stampActionId: selectedStamp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply stamp');
      if (data.output?.pdfBase64) {
        downloadBase64Pdf(data.output.pdfBase64, data.output.filename);
      }
      if (data.output?.stampedDocBase64) {
        downloadBase64Pdf(data.output.stampedDocBase64, data.output.stampedDocFilename);
      }
      backToDashboard();
    } catch (e) {
      alert(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const finalizeExternal = async () => {
  if (!bluebeamFile || !selectedItem) return;
  setFinalizingExternal(true);
  try {
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('finalFile', bluebeamFile);
    const res = await fetch(`${API_BASE}/api/ca-plus/submittals/${selectedItem._id}/finalize-external`, {
      method: 'PATCH',
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to finalize');
    setSelectedItem(data.submittal);
    setBluebeamFile(null);
  } catch (e) {
    alert(e.message);
  } finally {
    setFinalizingExternal(false);
  }
};

  const completeRfi = async () => {
    if (!selectedItem) return;
    const items = selectedItem.aiDraftResponse || [];
    const unacceptedCount = items.filter((r) => !r.accepted && !r.rejected).length;
    if (unacceptedCount > 0) {
      alert(`Please review and accept all ${items.length} suggested responses before finalizing this RFI (${unacceptedCount} still need review).`);
      return;
    }
    const comments = selectedItem.reviewerComments || [];
    const unacceptedComments = comments.filter((c) => !c.accepted).length;
    if (unacceptedComments > 0) {
      alert(`Please accept all ${comments.length} reviewer comments before finalizing this RFI (${unacceptedComments} still need review).`);
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/rfis/${selectedItem._id}/complete`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to complete RFI');
      if (data.output?.pdfBase64) {
        downloadBase64Pdf(data.output.pdfBase64, data.output.filename);
      }
      backToDashboard();
    } catch (e) {
      alert(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Dashboard (3-button view) ─────────────────────────────────────────
  if (adminView) {
    // isCaPlusAdmin is a UX convenience for showing/hiding this switch — the
    // real access control happens server-side in caPlusIntakeRoutes.js via
    // requireCaPlusAdmin, which checks the same FirmProfile.caPlusAdminEmails
    // list independently of anything the frontend does.
    return (
      <div>
        <span style={styles.backLink} onClick={() => setAdminView(false)}>← Back to My Reviews</span>
        <CaPlusIntakeTab user={user} />
      </div>
    );
  }

  if (view === 'dashboard') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#000000', margin: 0 }}>{appName}</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <a style={{ ...styles.secondaryBtn, textDecoration: 'none', display: 'inline-block' }} href={`${API_BASE}/api/ca-plus/digest/export-mine?userId=${encodeURIComponent(userId)}&reviewerName=${encodeURIComponent(reviewerName || '')}`}>
              ⬇ Export My Open Items
            </a>
            {isCaPlusAdmin && (
              <button style={styles.secondaryBtn} onClick={() => setAdminView(true)}>
                🗂️ Switch to Admin View
              </button>
            )}
            {offices.length > 1 && (
              <select
                value={officeFilter}
                onChange={(e) => setOfficeFilter(e.target.value)}
                style={{ ...styles.select, width: 'auto', marginBottom: 0, padding: '8px 12px' }}
              >
                <option value="">All Offices</option>
                {offices.map((o) => (
                  <option key={o.code} value={o.code}>{o.name || o.code}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {loading && <p style={{ color: '#6b7280' }}>Loading...</p>}
        {error && <p style={{ color: '#dc2626' }}>{error}</p>}

        {!loading && !error && (
          <div style={styles.buttonRow}>
            <button style={styles.bigButton(false)} onClick={() => openList('submittals')}>
              <div style={styles.bigButtonLabel}>📋 Submittals</div>
              <div style={styles.bigButtonCount}>{dashboardData.counts?.submittals ?? 0}</div>
            </button>
            {rfisEnabled && (
              <button style={styles.bigButton(false)} onClick={() => openList('rfis')}>
                <div style={styles.bigButtonLabel}>❓ RFIs</div>
                <div style={styles.bigButtonCount}>{dashboardData.counts?.rfis ?? 0}</div>
              </button>
            )}
            {forsEnabled && (
              <button style={styles.bigButton(false)} onClick={() => openList('fors')}>
                <div style={styles.bigButtonLabel}>🏗️ Field Reports</div>
                <div style={styles.bigButtonCount}>{dashboardData.counts?.fors ?? 0}</div>
              </button>
            )}
          </div>
        )}

      </div>
    );
  }

  // ─── List view (Submittals / RFIs / FORs) ──────────────────────────────
  if (view === 'list') {
    const allItems = dashboardData[listType] || [];
    const labels = { submittals: 'Submittals', rfis: 'RFIs', fors: 'Field Reports' };
    const q = (listSearch || '').toLowerCase();
    const filtered = q
      ? allItems.filter((item) => {
          const haystack = [
            item.submittalName, item.rfiName, item.forNumber,
            item.projectId?.projectName, item.projectId?.projectNumber,
            item.authorName, item.reviewerName,
          ].filter(Boolean).join(' ').toLowerCase();
          return haystack.includes(q);
        })
      : allItems;
    const items = [...filtered].sort((a, b) => {
      if (listSort === 'date-asc') return new Date(a.dateReceived || a.dateField || a.createdAt || 0) - new Date(b.dateReceived || b.dateField || b.createdAt || 0);
      if (listSort === 'date-desc') return new Date(b.dateReceived || b.dateField || b.createdAt || 0) - new Date(a.dateReceived || a.dateField || a.createdAt || 0);
      if (listSort === 'due-asc') {
        // Items with no due date (e.g. Field Reports) always sort last,
        // regardless of direction — there's nothing meaningful to rank them by.
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      }
      if (listSort === 'due-desc') {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(b.dueDate) - new Date(a.dueDate);
      }
      if (listSort === 'project') return (a.projectId?.projectNumber || '').localeCompare(b.projectId?.projectNumber || '');
      if (listSort === 'name') {
        const nameA = a.submittalName || a.rfiName || a.forNumber || '';
        const nameB = b.submittalName || b.rfiName || b.forNumber || '';
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    // Single row's JSX, extracted so it can render either flat (default
    // sorts) or nested under a project header (Project Number sort) —
    // per Randy's note that reviewers want to pick a project first, by
    // its admin-entered number/name, not by the raw submittal filename.
    const renderItemRow = (item) => {
      const name = item.submittalName || item.rfiName || item.forNumber || 'Untitled';
      const status = item.status || 'received';
      const itemDate = item.dateReceived || item.dateField || item.createdAt;
      return (
        <div key={item._id} style={styles.listRow} onClick={() => openDetail(item)}>
          <div>
            <div style={styles.listRowTitle}>{name}</div>
            <div style={styles.listRowMeta}>
              {item.projectId?.projectNumber ? `${item.projectId.projectNumber} — ` : ''}
              {item.projectId?.projectName || ''}
              {item.primarySpecSection ? ` · Spec ${item.primarySpecSection}` : ''}
            </div>
            {item.notes && (
              <div style={{
                fontSize: '11px', color: '#78350f', background: '#fffbeb', border: '1px solid #fde68a',
                borderRadius: '4px', padding: '2px 6px', marginTop: '4px', display: 'inline-block',
                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <strong>Note:</strong> {item.notes}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
            <span style={styles.badge(STATUS_COLORS[status] || '#6b7280')}>{status.replace('_', ' ')}</span>
            {itemDate && (
              <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                Received: {new Date(itemDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}
              </span>
            )}
            {item.dueDate && (() => {
              const daysUntilDue = Math.ceil((new Date(item.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
              const dueColor = daysUntilDue < 0 ? '#dc2626' : daysUntilDue <= dueDateWarningDays ? '#d97706' : '#9ca3af';
              const dueWeight = daysUntilDue <= dueDateWarningDays ? '700' : '400';
              return (
                <span style={{ fontSize: '11px', color: dueColor, fontWeight: dueWeight }}>
                  Due: {new Date(item.dueDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                </span>
              );
            })()}
            {(() => {
              const isReturned = status === 'returned' && typeof item.daysReviewed === 'number';
              const daysReviewed = isReturned
                ? item.daysReviewed
                : itemDate
                  ? Math.floor((new Date() - new Date(itemDate)) / (1000 * 60 * 60 * 24))
                  : null;
              if (daysReviewed === null) return null;
              const heat = daysReviewed <= 4
                ? { bg: '#dcfce7', text: '#166534' }
                : daysReviewed <= 7
                  ? { bg: '#fef9c3', text: '#854d0e' }
                  : { bg: '#fee2e2', text: '#991b1b' };
              return (
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', background: heat.bg, color: heat.text }}>
                  Days Reviewed: {daysReviewed}
                </span>
              );
            })()}
          </div>
        </div>
      );
    };

    // Grouped-by-project view — items are already sorted by project number
    // above, so grouping just needs to preserve that order while collapsing
    // consecutive items under the same project into one header.
    const groupedByProject = listSort === 'project' ? (() => {
      const groups = [];
      const byKey = new Map();
      items.forEach((item) => {
        const key = item.projectId?._id || item.projectId?.projectNumber || 'unassigned';
        if (!byKey.has(key)) {
          const group = {
            key,
            projectNumber: item.projectId?.projectNumber || '',
            projectName: item.projectId?.projectName || 'No Project Assigned',
            items: [],
          };
          byKey.set(key, group);
          groups.push(group);
        }
        byKey.get(key).items.push(item);
      });
      return groups;
    })() : null;

    return (
      <div>
        <span style={styles.backLink} onClick={() => window.history.back()}>← Back to {appName}</span>
        <h2 style={{ ...styles.sectionTitle, textTransform: 'uppercase' }}>{labels[listType]} ({items.length}{q ? ` of ${allItems.length}` : ''})</h2>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input
            style={{ ...styles.input, marginBottom: 0, flex: '1 1 240px' }}
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
            placeholder={`Search ${labels[listType].toLowerCase()}...`}
          />
          <select
            style={{ ...styles.input, marginBottom: 0, width: 'auto' }}
            value={listSort}
            onChange={(e) => setListSort(e.target.value)}
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="due-asc">Due Soonest</option>
            <option value="due-desc">Due Latest</option>
            <option value="project">Project Number</option>
            <option value="name">Name (A–Z)</option>
          </select>
        </div>
 {items.length === 0 && <p style={{ color: '#6b7280', fontSize: '14px' }}>Nothing open right now.</p>}
        {groupedByProject ? (
          groupedByProject.map((group) => (
            <div key={group.key} style={{ marginBottom: '22px' }}>
              <div style={{ fontWeight: '700', fontSize: '13px', color: '#111827', padding: '8px 2px 6px', borderBottom: '2px solid #e5e7eb', marginBottom: '4px' }}>
                {group.projectNumber ? `${group.projectNumber} — ` : ''}{group.projectName}
              </div>
              {group.items.map(renderItemRow)}
            </div>
          ))
        ) : (
          items.map(renderItemRow)
        )}
      </div>
    );
  }

    // ─── Field Report detail view (separate from Submittal/RFI review UI —
  // FORs are surfaced from Ops, not reviewed/stamped inside CA+) ──────────
  if (view === 'detail' && selectedItem && listType === 'fors') {
    const name = selectedItem.forNumber || 'Field Report';
    return (
      <div>
        <span style={styles.backLink} onClick={() => window.history.back()}>← Back to Field Reports</span>
        <div style={styles.card}>
          <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#000000', marginTop: 0 }}>{name}</h2>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
            {selectedItem.projectId?.projectName} {selectedItem.projectId?.projectNumber ? `(${selectedItem.projectId.projectNumber})` : ''}
            {selectedItem.worksite ? ` · ${selectedItem.worksite}` : ''}
          </div>
          <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
            <strong>Author:</strong> {selectedItem.authorName || 'N/A'}
          </div>
          <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
            <strong>Date:</strong> {selectedItem.dateField ? new Date(selectedItem.dateField).toLocaleDateString(undefined, { timeZone: 'UTC' }) : 'N/A'}
          </div>
          <div style={{ fontSize: '13px', color: '#374151', marginBottom: '16px' }}>
            <strong>Source:</strong> {selectedItem.source === 'ops' ? 'AECWorks Ops' : 'Manual entry'}
          </div>

          {selectedItem.pdfUrl ? (
            <>
              <a href={selectedItem.pdfUrl} target="_blank" rel="noopener noreferrer" style={styles.primaryBtn}>
                Open / Download PDF
              </a>
              <div style={{
                marginTop: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f9fafb',
              }}>
                <iframe
                  src={selectedItem.pdfUrl}
                  title={`${name} preview`}
                  style={{ width: '100%', height: '600px', border: 'none', display: 'block' }}
                />
              </div>
            </>
          ) : (
            <p style={{ fontSize: '13px', color: '#9ca3af' }}>
              {selectedItem.source === 'ops'
                ? 'No PDF has been generated for this report yet — generate one in the Ops portal to view it here.'
                : 'This is a manually-logged report with no attached PDF.'}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ─── Detail view ────────────────────────────────────────────────────────
  if (view === 'detail' && selectedItem) {
    const isSubmittal = listType === 'submittals';
    const name = selectedItem.submittalName || selectedItem.rfiName || 'Item';
    const aiComments = selectedItem.aiDraftComments || [];
    const aiResponse = selectedItem.aiDraftResponse;
    const reviewerComments = selectedItem.reviewerComments || [];

    return (
      <div>
        <span style={styles.backLink} onClick={() => window.history.back()}>← Back to {listType === 'submittals' ? 'Submittals' : listType === 'rfis' ? 'RFIs' : 'Field Reports'}</span>

        <div style={styles.card}>
          <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#000000', marginTop: 0 }}>{name}</h2>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
            {selectedItem.projectId?.projectName} {selectedItem.projectId?.projectNumber ? `(${selectedItem.projectId.projectNumber})` : ''}
            {selectedItem.primarySpecSection ? ` · Spec File ${selectedItem.primarySpecSection}` : ''}
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>
            {selectedItem.dateReceived ? `Received: ${new Date(selectedItem.dateReceived).toLocaleDateString(undefined, { timeZone: 'UTC' })}` : ''}
            {selectedItem.dueDate ? ` · Due: ${new Date(selectedItem.dueDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}` : ''}
            {selectedItem.dateReturned ? ` · Returned: ${new Date(selectedItem.dateReturned).toLocaleDateString(undefined, { timeZone: 'UTC' })}` : ''}
            {typeof selectedItem.daysReviewed === 'number' ? ` · Days Reviewed: ${selectedItem.daysReviewed}` : ''}
          </div>

          {/* Admin note — from CA Admin at intake or added later, for the
              reviewer's awareness only. Never included in the output report. */}
          {selectedItem.notes && (
            <div style={{ marginBottom: '16px', padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '5px', fontSize: '12px', color: '#78350f' }}>
              <strong>Admin Note:</strong> {selectedItem.notes}
            </div>
          )}

          {/* AI-drafted comments (submittal) */}
          {isSubmittal && aiComments.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={styles.sectionTitle}>CA+ Suggested Comments</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb', width: '25%' }}>Spec Section</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Details</th>
                    <th style={{ textAlign: 'center', padding: '8px', borderBottom: '2px solid #e5e7eb', width: '90px' }}>Accepted</th>
                  </tr>
                </thead>
                <tbody>
                  {aiComments.map((c) => (
                    <tr key={c._id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: c.rejected ? '#fef2f2' : c.accepted ? '#f0fdf4' : 'transparent' }}>
                      <td style={{ padding: '8px', fontWeight: '600', verticalAlign: 'top' }}>{c.specSection}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>
                        {editingAiCommentId === c._id ? (
                          <>
                            <div style={{ marginBottom: '6px', padding: '6px 8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '13px' }}>
                              <TrackedChangesText original={c.originalText} edited={editAiCommentText} />
                            </div>
                            <textarea
                              style={{ ...styles.textarea, marginBottom: '6px' }}
                              value={editAiCommentText}
                              onChange={(e) => setEditAiCommentText(e.target.value)}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button style={styles.primaryBtn} onClick={saveEditAiComment}>Save</button>
                              <button style={styles.secondaryBtn} onClick={() => setEditingAiCommentId(null)}>Cancel</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span style={c.rejected ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined}>
                              {!c.accepted && !c.rejected ? <TrackedChangesText original={c.originalText} edited={c.text} /> : c.text}
                            </span>
                            <div style={{ marginTop: '4px', display: 'flex', gap: '10px' }}>
                              {!c.rejected && (
                                <button
                                  style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                  onClick={() => startEditAiComment(c)}
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                style={{ background: 'none', border: 'none', color: c.rejected ? '#166534' : '#dc2626', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                onClick={() => toggleRejectAiComment(c._id, c.rejected)}
                              >
                                {c.rejected ? 'Restore' : 'Reject'}
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'top' }}>
                        <input type="checkbox" checked={!!c.accepted} disabled={c.rejected} onChange={() => toggleAcceptAiComment(c._id, c.accepted)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Drawing review note — only present when a drawing was included */}
          {isSubmittal && selectedItem.drawingReviewNote && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={styles.sectionTitle}>Drawing Review</h4>
              <div style={{ padding: '10px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px' }}>
                {selectedItem.drawingReviewNote}
              </div>
            </div>
          )}

          {/* AI-drafted response (RFI) */}
          {!isSubmittal && aiResponse.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={styles.sectionTitle}>CA+ Suggested Response</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>RFI #</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Spec Section</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Drawing Ref</th>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #e5e7eb' }}>Response</th>
                    <th style={{ textAlign: 'center', padding: '8px', borderBottom: '2px solid #e5e7eb', width: '90px' }}>Accepted</th>
                  </tr>
                </thead>
                <tbody>
                  {aiResponse.map((r) => (
                    <tr key={r._id} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: r.rejected ? '#fef2f2' : r.accepted ? '#f0fdf4' : 'transparent' }}>
                      <td style={{ padding: '8px', fontWeight: '600', verticalAlign: 'top' }}>{r.rfiId}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>{r.specSection}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>{r.drawingRef}</td>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>
                        {editingAiCommentId === r._id ? (
                          <>
                            <div style={{ marginBottom: '6px', padding: '6px 8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '13px' }}>
                              <TrackedChangesText original={r.originalResponse} edited={editAiCommentText} />
                            </div>
                            <textarea
                              style={{ ...styles.textarea, marginBottom: '6px' }}
                              value={editAiCommentText}
                              onChange={(e) => setEditAiCommentText(e.target.value)}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button style={styles.primaryBtn} onClick={saveEditAiComment}>Save</button>
                              <button style={styles.secondaryBtn} onClick={() => setEditingAiCommentId(null)}>Cancel</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <span style={r.rejected ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined}>
                              {!r.accepted && !r.rejected ? <TrackedChangesText original={r.originalResponse} edited={r.response} /> : r.response}
                            </span>
                            <div style={{ marginTop: '4px', display: 'flex', gap: '10px' }}>
                              {!r.rejected && (
                                <button
                                  style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                  onClick={() => startEditAiComment(r)}
                                >
                                  Edit
                                </button>
                              )}
                              <button
                                style={{ background: 'none', border: 'none', color: r.rejected ? '#166534' : '#dc2626', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                onClick={() => toggleRejectAiComment(r._id, r.rejected)}
                              >
                                {r.rejected ? 'Restore' : 'Reject'}
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'top' }}>
                        <input type="checkbox" checked={!!r.accepted} disabled={r.rejected} onChange={() => toggleAcceptAiComment(r._id, r.accepted)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Reviewer comments */}
          <div style={{ marginBottom: '16px' }}>
            <h4 style={styles.sectionTitle}>Add Reviewer Comments</h4>
            {reviewerComments.length === 0 && <p style={{ fontSize: '13px', color: '#9ca3af' }}>No comments yet.</p>}
            {reviewerComments.map((c) => (
              <div key={c._id} style={{ padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '6px', fontSize: '13px' }}>
                {editingCommentId === c._id ? (
                  <>
                    <textarea
                      style={styles.textarea}
                      value={editCommentText}
                      onChange={(e) => setEditCommentText(e.target.value)}
                    />
                    <div style={{ marginTop: '6px', display: 'flex', gap: '8px' }}>
                      <button style={styles.primaryBtn} onClick={saveEditComment}>Save</button>
                      <button style={styles.secondaryBtn} onClick={() => setEditingCommentId(null)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    {c.text}
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{c.addedBy}</span>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: c.accepted ? '#166534' : '#9ca3af', fontWeight: c.accepted ? '600' : '400' }}>
                          <input type="checkbox" checked={!!c.accepted} onChange={() => toggleAcceptComment(c._id, c.accepted)} />
                          Accepted
                        </label>
                        <button
                          style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                          onClick={() => startEditComment(c)}
                        >
                          Edit
                        </button>
                        <button
                          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                          onClick={() => deleteComment(c._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
            <textarea
              style={styles.textarea}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <div style={{ marginTop: '8px' }}>
              <button style={styles.secondaryBtn} onClick={addComment} disabled={actionLoading || !newComment.trim()}>
                + Add Comment
              </button>
            </div>
          </div>

          {/* Add a spec section and re-run the analysis */}
          {selectedItem.status !== 'returned' && (
            <div style={{ marginBottom: '16px', padding: '12px', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
              <h4 style={styles.sectionTitle}>Add a Spec File</h4>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                Realize a spec was missed? Attach it and the full analysis will re-run to include it.
              </p>
              {(() => {
                const primarySpec = isSubmittal ? selectedItem.primarySpecFileRef : selectedItem.projectDocsFileRef;
                const relatedSpecs = isSubmittal ? (selectedItem.specFileRefs || []) : (selectedItem.additionalSpecFileRefs || []);
                if (!primarySpec && relatedSpecs.length === 0) return null;
                return (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                      Currently included:
                    </div>
                    {primarySpec && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>• {primarySpec.originalname} <span style={{ color: '#9ca3af' }}>(Primary)</span></div>
                    )}
                    {relatedSpecs.map((s, i) => (
                      <div key={i} style={{ fontSize: '12px', color: '#6b7280' }}>• {s.originalname} <span style={{ color: '#9ca3af' }}>(Related)</span></div>
                    ))}
                  </div>
                );
              })()}
              <div style={{ marginBottom: '8px' }}>
                <label style={{ ...styles.secondaryBtn, marginLeft: 0, display: 'inline-block' }}>
                  + Choose Spec File
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setSpecFile(e.target.files[0] || null)}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              {specFile && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{specFile.name}</div>
              )}
              <button style={styles.secondaryBtn} onClick={addSpec} disabled={addingSpec || !specFile}>
                {addingSpec ? 'Adding...' : '+ Add Spec File & Re-Run Analysis'}
              </button>
              {specAddedMsg && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#166534' }}>✓ {specAddedMsg}</div>
              )}
            </div>
          )}

          {/* Add a drawing and re-run the analysis */}
          {selectedItem.status !== 'returned' && (
            <div style={{ marginBottom: '16px', padding: '12px', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
              <h4 style={styles.sectionTitle}>Add a Drawing</h4>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                Know a drawing that's relevant here? Attach it and the full analysis will re-run to include it.
              </p>
              {(selectedItem.drawingFileRefs || []).length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Currently attached ({selectedItem.drawingFileRefs.length}):
                  </div>
                  {selectedItem.drawingFileRefs.map((d, i) => (
                    <div key={i} style={{ fontSize: '12px', color: '#6b7280' }}>• {d.originalname}</div>
                  ))}
                  <button
                    style={{ ...styles.secondaryBtn, marginLeft: 0, marginTop: '6px', color: '#dc2626', borderColor: '#fca5a5', fontSize: '12px', padding: '6px 12px' }}
                    onClick={clearDrawings}
                    disabled={clearingDrawings}
                  >
                    {clearingDrawings ? 'Clearing...' : 'Clear All Drawings'}
                  </button>
                </div>
              )}
              <div style={{ marginBottom: '8px' }}>
                <label style={{ ...styles.secondaryBtn, marginLeft: 0, display: 'inline-block' }}>
                  + Choose Drawing
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    onChange={(e) => setDrawingFile(e.target.files[0] || null)}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              {drawingFile && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{drawingFile.name}</div>
              )}
              <button style={styles.secondaryBtn} onClick={addDrawing} disabled={addingDrawing || !drawingFile}>
                {addingDrawing ? 'Adding...' : '+ Add Drawing & Re-Run Analysis'}
              </button>
              {drawingAddedMsg && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#166534' }}>✓ {drawingAddedMsg}</div>
              )}
            </div>
          )}

          {/* Stamp (submittal only) */}
          {isSubmittal && selectedItem.status !== 'returned' && stampOptions.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={styles.sectionTitle}>Shop Drawing Stamp</h4>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Stamp Location</label>
                <select
                  style={{ ...styles.select, maxWidth: '320px' }}
                  value={selectedItem.submittalType || 'product'}
                  onChange={(e) => updateStampLocation(e.target.value)}
                >
                  <option value="product">Product Submittal (stamp on cover page)</option>
                  <option value="drawing">Drawing Submittal (stamp on each sheet)</option>
                </select>
              </div>
              <div style={styles.stampGrid}>
                {stampOptions.map((opt) => (
                  <div
                    key={opt.id}
                    style={styles.stampOption(selectedStamp === opt.id)}
                    onClick={() => setSelectedStamp(opt.id)}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
              {aiComments.filter((c) => !c.accepted && !c.rejected).length > 0 && (
                <p style={{ fontSize: '12px', color: '#b45309', marginTop: '10px', marginBottom: 0 }}>
                  ⚠️ {aiComments.filter((c) => !c.accepted && !c.rejected).length} of {aiComments.length} CA+ Suggested Comments still need to be reviewed above.
                </p>
              )}
              {reviewerComments.filter((c) => !c.accepted).length > 0 && (
                <p style={{ fontSize: '12px', color: '#b45309', marginTop: '10px', marginBottom: 0 }}>
                  ⚠️ {reviewerComments.filter((c) => !c.accepted).length} of {reviewerComments.length} reviewer comments still need to be accepted above.
                </p>
              )}
              <div style={{ marginTop: '12px' }}>
                <button
                  style={styles.primaryBtn}
                  onClick={applyStamp}
                  disabled={actionLoading || !selectedStamp || aiComments.filter((c) => !c.accepted && !c.rejected).length > 0 || reviewerComments.filter((c) => !c.accepted).length > 0}
                >
                  {actionLoading ? 'Applying...' : 'Apply Stamp & Return'}
                </button>
              </div>

              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px dashed #d1d5db' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                  Stamped each page independently in Bluebeam instead? Upload the finished file here to finalize and return it.
                </p>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ ...styles.secondaryBtn, marginLeft: 0, display: 'inline-block' }}>
                    + Choose File
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setBluebeamFile(e.target.files?.[0] || null)}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {bluebeamFile && (
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '10px' }}>{bluebeamFile.name}</span>
                  )}
                </div>
                <div>
                  <button
                    style={styles.primaryBtn}
                    onClick={finalizeExternal}
                    disabled={finalizingExternal || !bluebeamFile}
                  >
                    {finalizingExternal ? 'Finalizing...' : 'Finalize with Bluebeam File & Return'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Complete (RFI only) */}
          {!isSubmittal && selectedItem.status !== 'returned' && (
            <div>
              {aiResponse.filter((r) => !r.accepted && !r.rejected).length > 0 && (
                <p style={{ fontSize: '12px', color: '#b45309', marginTop: 0, marginBottom: '10px' }}>
                  ⚠️ {aiResponse.filter((r) => !r.accepted && !r.rejected).length} of {aiResponse.length} CA+ suggested responses still need to be reviewed above.
                </p>
              )}
              {reviewerComments.filter((c) => !c.accepted).length > 0 && (
                <p style={{ fontSize: '12px', color: '#b45309', marginTop: 0, marginBottom: '10px' }}>
                  ⚠️ {reviewerComments.filter((c) => !c.accepted).length} of {reviewerComments.length} reviewer comments still need to be accepted above.
                </p>
              )}
              <button
                style={styles.primaryBtn}
                onClick={completeRfi}
                disabled={actionLoading || aiResponse.filter((r) => !r.accepted && !r.rejected).length > 0 || reviewerComments.filter((c) => !c.accepted).length > 0}
              >
                {actionLoading ? 'Finalizing...' : 'Finalize & Return'}
              </button>
            </div>
          )}

          {selectedItem.status === 'returned' && (
            <div style={{ padding: '10px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', color: '#166534' }}>
              ✓ Returned {selectedItem.dateReturned ? `on ${new Date(selectedItem.dateReturned).toLocaleDateString(undefined, { timeZone: 'UTC' })}` : ''}
            </div>
          )}
        </div>

      </div>
    );
  }

  return null;
}